import { ID } from "@input-output-hk/essential-cardano-models";
import { useState } from "react";
import { fetchApi } from "../api-utils/fetcher";
import { ItemComment } from "../data/db/types";
import { KEYS } from "../utils/keyboard";
import useOnChangeHandler from "./useOnChangeHandler";

type ApiResponse = { comment: ItemComment };

type UpdateSource = {
  _id: ID;
};
type CreateSource = {
  about: ID;
  parentId?: ID;
};

type Source = CreateSource | UpdateSource;

type UseMutateCommentArgs = {
  initialValue?: string;
  onSuccess?(res: ApiResponse): void;
  clearOnSubmit?: boolean;
};

export default function useMutateComment(
  source: Source | undefined,
  {
    initialValue = "",
    onSuccess = () => {},
    clearOnSubmit = true
  }: UseMutateCommentArgs = {}
) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [value, handleChange, setValue] =
    useOnChangeHandler<HTMLTextAreaElement>(initialValue);
  const submit = async () => {
    try {
      setIsSubmitting(true);
      const res = await fetchApi<ApiResponse>("/api/comment", {
        method: "POST",
        data: {
          ...source,
          text: value
        }
      });
      if ("error" in res) {
        throw new Error(res.error);
      }
      onSuccess(res);
      if (clearOnSubmit) {
        setValue("");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (
      !e.defaultPrevented &&
      e.which === KEYS.enter &&
      !e.metaKey &&
      !e.shiftKey &&
      !e.ctrlKey &&
      !e.altKey
    ) {
      e.preventDefault();
      submit();
    }
  };
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    submit();
  };
  return {
    isSubmitting,
    value,
    setValue,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    click: { onClick: handleClick },
    submit
  };
}
